/**
 * Procedures tab: the element's maintenance entries (procedure and
 * crossProcedure) plus the four shipped timelines, each loaded on demand
 * through loadProcedure and started in the twin store; the player renders
 * above the list while a procedure runs.
 */
import { useMemo, useState } from 'react';
import { Play, Route, Wrench } from 'lucide-react';
import { Chip } from '@/components/ui/primitives';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { useTwin } from '../../store';
import { useJsonFile } from '../adapters/dataFiles';
import { EmptyState, SectionTitle } from '../components/layout';
import { TIMELINE_IDS, loadProcedure } from '../procedures/loadProcedures';
import { ProcedurePlayer } from '../procedures/ProcedurePlayer';

interface CatalogueEntry {
  id: string;
  label: string;
  kind: 'procedure' | 'crossProcedure' | 'catalogue';
  scene?: string;
}

interface DataFlowKnowledge {
  procedures?: Record<string, { name: string; description: string; duration: string; type: string }>;
}

const FALLBACK_LABEL: Record<string, string> = {
  TimelineAntennaFlow: 'Antenna Data Flow (Receive)',
  TimelineAntennaFlowTransmit: 'Antenna Data Flow (Transmit)',
  TimelineAntennaMaintenance: 'Antenna Maintenance',
  TimelineDataFlow: 'Data Flow (satellite to end user)',
};

export function ProceduresPanel() {
  const element = useTwin((s) => s.element);
  const running = useTwin((s) => s.procedure);
  const startProcedure = useTwin((s) => s.startProcedure);
  const knowledge = useJsonFile<DataFlowKnowledge>('/data/knowledge/data-flow.json');
  const [loading, setLoading] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const entries = useMemo<CatalogueEntry[]>(() => {
    const out: CatalogueEntry[] = [];
    for (const link of element?.maintenance ?? []) {
      if (link.type !== 'procedure' && link.type !== 'crossProcedure') continue;
      if (out.some((e) => e.id === link.href)) continue;
      out.push({ id: link.href, label: link.label, kind: link.type as 'procedure' | 'crossProcedure', scene: link.scene });
    }
    for (const id of TIMELINE_IDS) {
      if (out.some((e) => e.id === id)) continue;
      out.push({ id, label: knowledge.data?.procedures?.[id]?.name ?? FALLBACK_LABEL[id] ?? id, kind: 'catalogue' });
    }
    return out;
  }, [element, knowledge.data]);

  if (!element) return <EmptyState title="No twin loaded." className="m-3" />;

  const play = async (entry: CatalogueEntry) => {
    setLoading(entry.id);
    setErrors((e) => ({ ...e, [entry.id]: '' }));
    try {
      const procedure = await loadProcedure(entry.id, { assetId: element.id, subsystems: element.subsystems });
      startProcedure(procedure);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      console.error(`[twin] procedure ${entry.id} failed to load:`, err);
      setErrors((e) => ({ ...e, [entry.id]: message }));
      toast('error', `Procedure ${entry.label} did not load: ${message}`);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-3 text-xs">
      {running && <ProcedurePlayer />}
      <div>
        <SectionTitle right={<span className="font-mono text-[10px] text-ink-3">{entries.length}</span>}>Available procedures</SectionTitle>
        <ul className="flex flex-col gap-1">
          {entries.map((entry) => {
            const info = knowledge.data?.procedures?.[entry.id];
            const active = running?.procedure.id === entry.id;
            const Icon = entry.kind === 'procedure' || entry.id.includes('Maintenance') ? Wrench : Route;
            return (
              <li key={entry.id} data-procedure-id={entry.id} className={cn('rounded-control border px-3 py-2', active ? 'border-accent/60 bg-accent-soft/40' : 'border-hairline bg-raised')}>
                <div className="flex items-start gap-2">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-ink">{entry.label}</span>
                      <Chip tone="neutral" className="px-1.5 py-0 text-[9px]">
                        {entry.kind === 'crossProcedure' ? 'Cross-scene' : entry.kind === 'procedure' ? 'In scene' : 'Catalogue'}
                      </Chip>
                      {info?.duration && <span className="font-mono text-[10px] text-ink-3">{info.duration}</span>}
                    </div>
                    {info?.description && <p className="mt-0.5 leading-snug text-ink-3">{info.description}</p>}
                    {errors[entry.id] && <p className="mt-0.5 text-warning">{errors[entry.id]}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => void play(entry)}
                    disabled={loading !== null}
                    className="inline-flex shrink-0 items-center gap-1 rounded-control bg-accent px-2 py-1 text-[11px] font-medium text-accent-ink hover:brightness-95 disabled:opacity-40"
                  >
                    <Play className="h-3 w-3" /> {loading === entry.id ? 'Loading' : active ? 'Restart' : 'Start'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        <p className="mt-2 text-[10px] leading-snug text-ink-3">
          Start opens the first step and waits. Use Next and Previous at your own pace, Replay to restore a step’s view, or Play for automatic advance. Esc stops.
        </p>
      </div>
      {element.documents && element.documents.length > 0 && (
        <div>
          <SectionTitle>Documents</SectionTitle>
          <ul className="flex flex-col gap-0.5">
            {element.documents.map((d) => (
              <li key={d.href}>
                <a href={d.href} target="_blank" rel="noopener noreferrer" className="text-ink-2 underline decoration-hairline-strong hover:text-accent">
                  {d.label} <span className="text-[10px] text-ink-3">({d.type})</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
